import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  FlaskConical, RefreshCw, Trash2, Copy, Check, BookOpen, ExternalLink,
  Rocket, RotateCcw, AlertTriangle, KeyRound,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function SandboxSection() {
  const [loading, setLoading] = useState(true);
  const [env, setEnv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [secretDialog, setSecretDialog] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const docsUrl = `${window.location.origin}/api/v1/docs`;
  const redocUrl = `${window.location.origin}/api/v1/redoc`;
  const sandboxBase = `${window.location.origin}/api/v1/sandbox`;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/sandbox`, { headers: authHeader() });
      setEnv(r.data?.exists ? r.data : null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const provision = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/portal/sandbox/provision`, {}, { headers: authHeader() });
      if (r.data?.raw_token) {
        setSecretDialog({ token: r.data.raw_token, site: r.data.site, base_url: sandboxBase });
      } else {
        toast.info('Sandbox existiert bereits. Benutze "Token rotieren", um ein neues Secret zu erzeugen.');
      }
      await load();
    } catch (e) {
      toast.error('Sandbox konnte nicht erstellt werden');
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/portal/sandbox/reset`, {}, { headers: authHeader() });
      toast.success('Demo-Daten zurückgesetzt');
      await load();
    } catch (e) {
      toast.error('Reset fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const rotate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/portal/sandbox/rotate-token`, {}, { headers: authHeader() });
      setSecretDialog({ token: r.data.raw_token, site: env?.site, base_url: sandboxBase });
      toast.success('Neuer Sandbox-Token erzeugt');
      await load();
    } catch (e) {
      toast.error('Rotation fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/portal/sandbox`, { headers: authHeader() });
      toast.success('Sandbox gelöscht');
      setEnv(null);
      setConfirmDelete(false);
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch (e) { /* noop */ }
  };

  if (loading) return null;

  return (
    <Card data-testid="sandbox-section">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-orange-500" /> API Sandbox &amp; Dokumentation
          </CardTitle>
          <CardDescription>
            Isolierte Demo-Umgebung mit vorgefüllten Testdaten · vollständige OpenAPI-Referenz
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <a href={docsUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="sandbox-docs-btn">
              <BookOpen className="h-4 w-4 mr-2" /> Swagger UI
              <ExternalLink className="h-3 w-3 ml-1 text-muted-foreground" />
            </Button>
          </a>
          <a href={redocUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="sandbox-redoc-btn">
              <BookOpen className="h-4 w-4 mr-2" /> ReDoc
              <ExternalLink className="h-3 w-3 ml-1 text-muted-foreground" />
            </Button>
          </a>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!env && (
          <div className="rounded-lg border border-dashed bg-orange-50/40 p-6 text-center space-y-3" data-testid="sandbox-empty-state">
            <div className="mx-auto w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Rocket className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold">Sandbox in 1 Klick erzeugen</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Wir legen eine isolierte Demo-Site an, seed-en 5 Materialien + 2 Gesuche + 1 Order und
                geben Dir einen dedizierten Token. Keine Berührung mit Echtdaten.
              </p>
            </div>
            <Button onClick={provision} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="sandbox-provision-btn">
              <Rocket className="h-4 w-4 mr-2" /> Sandbox &amp; Token erzeugen
            </Button>
          </div>
        )}

        {env && (
          <div className="space-y-4" data-testid="sandbox-provisioned">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoRow label="Sandbox-Site" value={env.site?.site_name || '—'} mono={false} />
              <InfoRow label="Site-ID" value={env.site?.site_id || '—'} mono />
              <InfoRow
                label="Basis-URL"
                value={sandboxBase}
                mono
                onCopy={() => copy(sandboxBase, 'base')}
                copied={copiedKey === 'base'}
              />
              <InfoRow
                label="Token-Prefix"
                value={env.token?.token_prefix || '—'}
                mono
                onCopy={() => copy(env.token?.token_prefix || '', 'prefix')}
                copied={copiedKey === 'prefix'}
              />
            </div>

            <div className="rounded-md bg-slate-50 border p-3 text-xs space-y-1">
              <p className="font-medium text-slate-700 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Aus Sicherheitsgründen zeigen wir den Secret-Teil nur einmal nach dem Erzeugen.
                Für ein neues Secret bitte <em>Token rotieren</em>.
              </p>
            </div>

            <div className="rounded-lg bg-slate-950 text-slate-100 p-4 font-mono text-xs overflow-x-auto" data-testid="sandbox-example">
              <p className="text-slate-400 mb-2"># Ping the sandbox to verify your setup</p>
              <p>curl -X GET "{sandboxBase}/ping" \<br />
                &nbsp;&nbsp;-H "Authorization: Bearer {env.token?.token_prefix}.&lt;SECRET&gt;"</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={reset} disabled={busy} variant="outline" size="sm" data-testid="sandbox-reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Demo-Daten zurücksetzen
              </Button>
              <Button onClick={rotate} disabled={busy} variant="outline" size="sm" data-testid="sandbox-rotate-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Token rotieren
              </Button>
              <Button onClick={() => setConfirmDelete(true)} disabled={busy} variant="outline" size="sm" className="text-red-600" data-testid="sandbox-delete-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Sandbox löschen
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Secret reveal dialog */}
      <Dialog open={!!secretDialog} onOpenChange={(v) => !v && setSecretDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="sandbox-secret-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-orange-500" /> Ihr Sandbox-Token (nur jetzt sichtbar)
            </DialogTitle>
            <DialogDescription>
              Kopieren Sie den Token jetzt. Nach Schließen dieses Dialogs bleibt nur der Prefix sichtbar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-slate-950 text-slate-100 p-3 font-mono text-xs break-all" data-testid="sandbox-secret-value">
              {secretDialog?.token}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copy(secretDialog?.token || '', 'secret')}
              data-testid="sandbox-secret-copy"
            >
              {copiedKey === 'secret' ? <Check className="h-4 w-4 mr-2 text-green-600" /> : <Copy className="h-4 w-4 mr-2" />}
              {copiedKey === 'secret' ? 'Kopiert' : 'Token kopieren'}
            </Button>
            <div className="rounded bg-orange-50 border border-orange-200 p-3 text-xs">
              <p className="font-medium mb-1">Nächste Schritte:</p>
              <ol className="list-decimal ml-4 space-y-0.5 text-muted-foreground">
                <li>Öffne <a className="underline" href={docsUrl} target="_blank" rel="noreferrer">Swagger UI</a> und klicke "Authorize"</li>
                <li>Füge den Bearer-Token ein</li>
                <li>Probiere z.&nbsp;B. <code>GET /sandbox/materials</code> aus</li>
              </ol>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setSecretDialog(null)} data-testid="sandbox-secret-close">Verstanden</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-sm" data-testid="sandbox-delete-dialog">
          <DialogHeader>
            <DialogTitle>Sandbox wirklich löschen?</DialogTitle>
            <DialogDescription>
              Dies entfernt Deine Sandbox-Site, Token und alle Demo-Daten. Du kannst jederzeit eine neue erzeugen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={remove} disabled={busy} data-testid="sandbox-delete-confirm">
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function InfoRow({ label, value, mono = false, onCopy, copied }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className={`text-sm ${mono ? 'font-mono' : 'font-medium'}`}>{value}</p>
      </div>
      {onCopy && (
        <button
          onClick={onCopy}
          className="ml-auto text-muted-foreground hover:text-foreground"
          title="Kopieren"
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}
